import {
    Alert,
    Badge,
    Card,
    Code,
    Group,
    Stack,
    Table,
    Text,
} from '@mantine/core';

import {
    ExpectedOutcome,
    TestStep,
} from '../types.ts';

const OUTCOME_COLOR: Record<ExpectedOutcome, string> = {
    [ExpectedOutcome.EXPECT_PASS]: 'green',
    [ExpectedOutcome.EXPECT_FAIL]: 'red',
    [ExpectedOutcome.EXPECT_SKIP]: 'gray',
};

// Read-only view of the authored steps, in stored order.
//
// Sorted by `stepNumber` rather than trusting array order, for the same reason
// buildInitialValues does: the number is what the executor runs on, so the
// profile must agree with it rather than with however the API happened to
// return the list.
export const StepsList = ({steps}: { steps?: TestStep[] | null }) => {

    const ordered = [...(steps ?? [])].sort((a, b) => (a.stepNumber ?? 0) - (b.stepNumber ?? 0));

    if (ordered.length === 0) {
        return (
            <Alert color='gray' variant='light'>
                No steps. This test definition does nothing when run.
            </Alert>
        );
    }

    return (
        <Stack gap={'xs'}>
            {ordered.map((step, index) => (
                <Card key={index} withBorder padding={'sm'}>
                    <Stack gap={'xs'}>
                        <Group justify={'space-between'}>
                            <Group gap={'xs'}>
                                <Badge variant='light'>{`Step ${step.stepNumber}`}</Badge>
                                <Code>{step.actionCode}</Code>
                            </Group>
                            <Badge
                                variant='light'
                                color={OUTCOME_COLOR[step.expect] ?? 'gray'}
                            >
                                {step.expect}
                            </Badge>
                        </Group>

                        {(step.inputs ?? []).length > 0 ? (
                            <Table withTableBorder verticalSpacing={'xs'}>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Key</Table.Th>
                                        <Table.Th>Value</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {(step.inputs ?? []).map((input, inputIndex) => (
                                        <Table.Tr key={inputIndex}>
                                            <Table.Td><Code>{input.key}</Code></Table.Td>
                                            <Table.Td>{input.value}</Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        ) : (
                            <Text size={'xs'} c={'dimmed'}>No inputs</Text>
                        )}
                    </Stack>
                </Card>
            ))}
        </Stack>
    );
};
